import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import { useCurrency } from '../context/CurrencyContext';
import { formatPrice } from '../utils/helpers';
import { getSelectedOptionsList } from '../utils/productOptions';
import { motion } from 'framer-motion';
import api from '../utils/api';

const OrderHistory = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { exchangeRates, selectedCurrency } = useCurrency();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    const fetchOrders = async () => {
      setLoading(true);
      try {
        const params = filter !== 'all' ? { status: filter } : {};
        const response = await api.get('/orders', { params });
        setOrders(response.data.orders);
      } catch (error) {
        console.error('Error fetching orders:', error);
        toast.error('Failed to load orders');
      } finally {
        setLoading(false);
      }
    };

    if (!user) {
      navigate('/login');
      return;
    }

    fetchOrders();
  }, [filter, navigate, user]);

  const getStatusColor = (status) => {
    const colors = {
      pending: 'text-yellow-500',
      processing: 'text-blue-500',
      shipped: 'text-purple-500',
      delivered: 'text-green-500',
      cancelled: 'text-red-500',
    };
    return colors[status] || 'text-gray-400';
  };

  const getPaymentStatusColor = (status) => {
    const colors = {
      pending: 'text-yellow-500',
      completed: 'text-green-500',
      failed: 'text-red-500',
      refunded: 'text-orange-500',
    };
    return colors[status] || 'text-gray-400';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-gold mx-auto mb-4"></div>
          <p className="text-white text-lg">Loading orders...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black py-8 sm:py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="heading-metallic mb-2 text-3xl sm:text-4xl">Order History</h1>
          <p className="text-gray-400">View and track your orders</p>
        </motion.div>

        {/* Filter Tabs */}
        <div className="flex gap-2 mb-8 overflow-x-auto">
          {['all', 'pending', 'processing', 'shipped', 'delivered', 'cancelled'].map((status) => (
            <button
              key={status}
              onClick={() => setFilter(status)}
              className={`rounded-full px-4 py-2 text-sm font-semibold capitalize whitespace-nowrap transition-all duration-200 ${
                filter === status
                  ? 'bg-metallic-gold text-black shadow-gold-metal'
                  : 'border border-gray-700 bg-gold-500/10 text-gray-300 hover:border-gray-700 hover:text-gold-100'
              }`}
            >
              {status}
            </button>
          ))}
        </div>

        {/* Orders List */}
        {orders.length === 0 ? (
          <div className="bg-gradient-to-r from-black via-gray-900 to-black p-12 rounded-lg border border-gray-800 text-center">
            <p className="text-gray-400 text-lg mb-4">No orders found</p>
            <button
              onClick={() => navigate('/products')}
              className="btn-primary"
            >
              Start Shopping
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {orders.map((order) => (
              <motion.div
                key={order.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-gradient-to-r from-black via-gray-900 to-black p-6 rounded-lg border border-gray-800"
              >
                <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4">
                  <div>
                    <h3 className="heading-metallic-soft text-xl mb-1">
                      Order #{order.order_number}
                    </h3>
                    <p className="text-sm text-gray-400">
                      Placed on {formatDate(order.created_at)}
                    </p>
                  </div>
                  <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:gap-4 md:mt-0">
                    <div className="text-left sm:text-right">
                      <p className="text-xs text-gray-400 mb-1">Order Status</p>
                      <p className={`font-bold capitalize ${getStatusColor(order.status)}`}>
                        {order.status}
                      </p>
                    </div>
                    <div className="text-left sm:text-right">
                      <p className="text-xs text-gray-400 mb-1">Payment</p>
                      <p className={`font-bold capitalize ${getPaymentStatusColor(order.payment_status)}`}>
                        {order.payment_status}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="border-t border-gray-700 pt-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    <div>
                      <p className="text-xs text-gray-400 mb-1">Total Amount</p>
                      <p className="text-lg font-bold text-white">
                        {formatPrice(order.total, selectedCurrency, exchangeRates)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 mb-1">Payment Method</p>
                      <p className="text-white capitalize">{order.payment_method || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 mb-1">Items</p>
                      <p className="text-white">
                        {order.items?.length || 0} item{order.items?.length !== 1 ? 's' : ''}
                      </p>
                    </div>
                  </div>

                  {/* Order Items Preview */}
                  {order.items && order.items.length > 0 && (
                    <div className="bg-black p-4 rounded-lg">
                      <div className="space-y-2">
                        {order.items.slice(0, 3).map((item, index) => (
                          <div key={index} className="flex flex-col gap-2 text-sm sm:flex-row sm:justify-between sm:gap-4">
                            <span className="text-gray-300">
                              <span>{item.name} × {item.quantity}</span>
                              {getSelectedOptionsList(item.selected_options).map((option) => (
                                <span key={`${option.name}-${option.value}`} className="block text-xs text-gray-500">
                                  {option.name}: {option.value}
                                </span>
                              ))}
                            </span>
                            <span className="text-white sm:text-right">
                              {formatPrice(parseFloat(item.price) * item.quantity, selectedCurrency, exchangeRates)}
                            </span>
                          </div>
                        ))}
                        {order.items.length > 3 && (
                          <p className="text-xs text-gray-500 text-center pt-2">
                            And {order.items.length - 3} more item{order.items.length - 3 !== 1 ? 's' : ''}
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Tracking info */}
                {order.shipped_at && (
                  <div className="mt-4 p-3 bg-blue-900 bg-opacity-20 border border-blue-500 rounded-lg">
                    <p className="text-sm text-blue-400">
                      📦 Shipped on {formatDate(order.shipped_at)}
                      {order.delivered_at && ` • Delivered on ${formatDate(order.delivered_at)}`}
                    </p>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderHistory;
